import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import {
  Building2,
  GitBranch,
  Users,
  ArrowRight,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Plus,
  Link2,
  BarChart3,
  UserCircle
} from 'lucide-react';

export default function PortfolioPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [sharedRates, setSharedRates] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [newGroup, setNewGroup] = useState({ group_code: '', group_name: '', group_type: 'region', description: '' });
  const [newRate, setNewRate] = useState({ rate_code: '', rate_name: '', rate_type: 'corporate', base_amount: '', currency: 'ETB' });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, groupsRes, ratesRes, transfersRes] = await Promise.all([
        api.get('/api/portfolio/dashboard'),
        api.get('/api/portfolio/groups'),
        api.get('/api/portfolio/shared-rates'),
        api.get('/api/portfolio/transfers')
      ]);
      setDashboardData(dashRes.data);
      setGroups(groupsRes.data || []);
      setSharedRates(ratesRes.data || []);
      setTransfers(transfersRes.data || []);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      await api.post('/api/portfolio/groups', newGroup);
      toast({ title: 'Property group created' });
      setShowGroupDialog(false);
      setNewGroup({ group_code: '', group_name: '', group_type: 'region', description: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateRate = async () => {
    try {
      await api.post('/api/portfolio/shared-rates', {
        ...newRate,
        base_amount: parseFloat(newRate.base_amount)
      });
      toast({ title: 'Shared rate created' });
      setShowRateDialog(false);
      setNewRate({ rate_code: '', rate_name: '', rate_type: 'corporate', base_amount: '', currency: 'ETB' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleApproveTransfer = async (transferId) => {
    try {
      await api.put(`/api/portfolio/transfers/${transferId}/approve`);
      toast({ title: 'Transfer approved' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getTransferStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'default';
      case 'completed': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Portfolio Management</h1>
          <p className="text-muted-foreground">Manage multiple properties from a central platform</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">Property Groups</TabsTrigger>
          <TabsTrigger value="shared-rates" data-testid="tab-shared-rates">Shared Rates</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">Transfers</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Properties</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.properties?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Total properties</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Occupancy</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.portfolio_kpis?.occupancy_rate?.toFixed(1) || 0}%
                </div>
                <p className="text-xs text-muted-foreground">Portfolio average</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">RevPAR</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.portfolio_kpis?.revpar?.toLocaleString() || 0} ETB
                </div>
                <p className="text-xs text-muted-foreground">Portfolio average</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(dashboardData?.portfolio_kpis?.total_revenue / 1000)?.toFixed(0) || 0}K
                </div>
                <p className="text-xs text-muted-foreground">Total revenue (ETB)</p>
              </CardContent>
            </Card>
          </div>

          {/* Property Groups & Pending Transfers */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5" />
                  Property Groups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.groups?.items?.slice(0, 5).map((group) => (
                    <div key={group.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{group.group_name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{group.group_type}</p>
                      </div>
                      <Badge variant="outline">{group.group_code}</Badge>
                    </div>
                  ))}
                  {(!dashboardData?.groups?.items || dashboardData.groups.items.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No property groups</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5" />
                  Pending Transfers ({dashboardData?.pending_transfers?.count || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.pending_transfers?.items?.slice(0, 5).map((transfer) => (
                    <div key={transfer.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{transfer.transfer_type.replace('_', ' ')}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(transfer.transfer_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleApproveTransfer(transfer.id)}>
                        Approve
                      </Button>
                    </div>
                  ))}
                  {(!dashboardData?.pending_transfers?.items || dashboardData.pending_transfers.items.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No pending transfers</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Properties List */}
          <Card>
            <CardHeader>
              <CardTitle>Properties Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {dashboardData?.properties?.items?.slice(0, 6).map((property) => (
                  <div key={property.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Building2 className="h-8 w-8 text-primary" />
                    <div>
                      <p className="font-medium">{property.name}</p>
                      <p className="text-sm text-muted-foreground">{property.code}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Property Groups</h2>
            <Button onClick={() => setShowGroupDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {groups.map((group) => (
              <Card key={group.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{group.group_name}</CardTitle>
                    <Badge variant="outline">{group.group_code}</Badge>
                  </div>
                  <CardDescription className="capitalize">{group.group_type}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{group.description || 'No description'}</p>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm">
                      <Link2 className="h-4 w-4 mr-2" />
                      Manage Members
                    </Button>
                    <Button variant="outline" size="sm">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      View Metrics
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {groups.length === 0 && (
              <Card className="col-span-2">
                <CardContent className="text-center py-8 text-muted-foreground">
                  No property groups. Create a group to organize your properties.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="shared-rates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Shared Rate Codes</h2>
            <Button onClick={() => setShowRateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Shared Rate
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rate Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Base Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sharedRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell className="font-mono">{rate.rate_code}</TableCell>
                    <TableCell>{rate.rate_name}</TableCell>
                    <TableCell className="capitalize">{rate.rate_type}</TableCell>
                    <TableCell>{rate.base_amount?.toLocaleString()}</TableCell>
                    <TableCell>{rate.currency}</TableCell>
                    <TableCell>
                      <Badge variant={rate.is_active ? 'default' : 'secondary'}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {sharedRates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No shared rates. Create a rate code to share across properties.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="transfers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Cross-Property Transfers</h2>
            <Button onClick={() => setShowTransferDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Transfer
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Transfer Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell className="capitalize">{transfer.transfer_type.replace('_', ' ')}</TableCell>
                    <TableCell>{new Date(transfer.transfer_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={getTransferStatusColor(transfer.status)}>
                        {transfer.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(transfer.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {transfer.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleApproveTransfer(transfer.id)}>
                            Approve
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {transfers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No transfers. Create a transfer request to move guests between properties.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Property Group</DialogTitle>
            <DialogDescription>Group properties for centralized management</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Group Code</Label>
                <Input
                  placeholder="EAST-REGION"
                  value={newGroup.group_code}
                  onChange={(e) => setNewGroup({ ...newGroup, group_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Group Name</Label>
                <Input
                  placeholder="Eastern Region"
                  value={newGroup.group_name}
                  onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Group Type</Label>
              <Select
                value={newGroup.group_type}
                onValueChange={(value) => setNewGroup({ ...newGroup, group_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">Region</SelectItem>
                  <SelectItem value="brand">Brand</SelectItem>
                  <SelectItem value="ownership">Ownership</SelectItem>
                  <SelectItem value="cluster">Cluster</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Description..."
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup}>Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Shared Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Shared Rate</DialogTitle>
            <DialogDescription>Create a rate code shared across properties</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate Code</Label>
                <Input
                  placeholder="CORP-2024"
                  value={newRate.rate_code}
                  onChange={(e) => setNewRate({ ...newRate, rate_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Rate Name</Label>
                <Input
                  placeholder="Corporate 2024"
                  value={newRate.rate_name}
                  onChange={(e) => setNewRate({ ...newRate, rate_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate Type</Label>
                <Select
                  value={newRate.rate_type}
                  onValueChange={(value) => setNewRate({ ...newRate, rate_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="negotiated">Negotiated</SelectItem>
                    <SelectItem value="package">Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Base Amount</Label>
                <Input
                  type="number"
                  placeholder="5000"
                  value={newRate.base_amount}
                  onChange={(e) => setNewRate({ ...newRate, base_amount: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRate}>Create Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
